import React, { useState, useEffect } from "react";
import { Container } from "reactstrap";
import { Button, Grid, TextField, Typography } from "@mui/material";
import axios from "axios";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Moment from "moment";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";

const LetterOfNecessityForm = (props) => {
  const {
    FormId,
    FormPatientId,
    PatientName,
    onFormSubmit,
    handleClose,
    PatientDob,
  } = props;

  const [patientDOB, setPatientDOB] = useState(
    Moment(PatientDob).format("YYYY-MM-DD")
  );
  const [physicianDate, setPhysicianDate] = useState(
    Moment().format("YYYY-MM-DD")
  );

  const fields = [
    "id",
    "patientId",
    "practiceId",
    "patientName",
    "patientDOB",
    "diagnosis",
    "oralApplianceType",
    "medicalNecessityStatement",
    "physicianName",
    "physicianAddress",
    "physicianSignature",
    "physicianNPI",
    "physicianDate",
    "createdDate",
    "createdBy",
    "updatedDate",
    "updatedBy",
    "inactive",
  ];

  const defValues = {
    id: 0,
    patientId: 0,
    practiceId: 0,
    patientName: "",
    patientDOB: "",
    diagnosis: "",
    oralApplianceType: "",
    medicalNecessityStatement: "",
    physicianName: "",
    physicianAddress: "",
    physicianSignature: "",
    physicianNPI: "",
    physicianDate: "",
    createdDate: "",
    createdBy: "",
    updatedDate: "",
    updatedBy: "",
    inactive: false,
  };

  const token = localStorage.getItem("Token");
  const auth = `Bearer ${token}`;
  const headers = { Authorization: auth };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: defValues,
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (FormId) {
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/PatientForms/GetFormLetterOfNecessityId?id=${FormId}`,
          { headers }
        )
        .then((response) => {
          fields.forEach((field) => {
            if (response[field] !== undefined) {
              setValue(field, response[field]);
            }
          });
          if (response.physicianDate) {
            setPhysicianDate(
              Moment(response.physicianDate).format("YYYY-MM-DD")
            );
          }
        })
        .catch((error) => {
          console.error("Error loading form data:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to load form data",
          });
        });
    }
  }, [FormId]);

  const onSubmit = (data) => {
    setSubmitting(true);

    const postData = {
      ...data,
      patientId: FormPatientId,
      patientDOB: patientDOB,
      patientName: PatientName,
    };

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/PatientForms/AddFormLetterOfNecessity`,
        postData,
        { headers }
      )
      .then((response) => {
        Swal.fire({
          icon: "success",
          title: "Form Added!",
          html: `<p>Letter of Medical Necessity has been saved successfully.</p>`,
          confirmButtonColor: "#2196F3",
          willOpen: () => {
            const swalPopup = document.querySelector(".swal2-container");
            if (swalPopup) swalPopup.style.zIndex = 2200;
          },
        });

        if (onFormSubmit) {
          onFormSubmit(response.id);
        }

        if (handleClose) {
          handleClose();
        }
        setSubmitting(false);
      })
      .catch((error) => {
        setSubmitting(false);
        Swal.fire({
          icon: "error",
          title: "Failed!",
          html: `<p>${
            error.response?.data?.message ||
            "Something went wrong. Please try again."
          }</p>`,
          confirmButtonColor: "#F44336",
          willOpen: () => {
            const swalPopup = document.querySelector(".swal2-container");
            if (swalPopup) swalPopup.style.zIndex = 2200;
          },
        });
      });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4,}}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header Section */}
        <Grid container spacing={3} sx={{ m: 2 }}>
          <Grid item xs={12}>
            <Typography
              variant="h4"
              align="center"
              fontWeight="bold"
              color="primary"
              gutterBottom
            >
              LETTER OF MEDICAL NECESSITY
            </Typography>
            <Typography
              variant="h6"
              align="center"
              fontWeight="medium"
              sx={{ mb: 3 }}
            >
              Referring MD's Written Order for Oral Appliance for OSA
            </Typography>
          </Grid>
        </Grid>

        {/* Patient Information Section */}
        <Grid
          container
          spacing={3}
          sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
        >
          <Grid item xs={12}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary"
              gutterBottom
            >
              Patient Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="patientName"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Patient Name"
                  size="small"
                  fullWidth
                  variant="outlined"
                  value={PatientName}
                  InputProps={{ readOnly: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="patientDOB"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Date of Birth"
                  type="date"
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={patientDOB || ""}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setPatientDOB(e.target.value);
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Diagnosis Section */}
        <Grid
          container
          spacing={3}
          sx={{m: 2, p: 2, backgroundColor: "#e8f4fd", borderRadius: 2 }}
        >
          <Grid item xs={12}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary"
              gutterBottom
            >
              Diagnosis
            </Typography>
            <Controller
              name="diagnosis"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Diagnosis"
                  size="small"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={2}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Oral Appliance Type Section */}
        <Grid
          container
          spacing={3}
          sx={{m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
        >
          <Grid item xs={12}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary"
              gutterBottom
            >
              Oral Appliance Type
            </Typography>
            <Controller
              name="oralApplianceType"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Oral Appliance Type"
                  size="small"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={4}
                  {...field}
                />
              )}
            />
            <Typography variant="body2" sx={{ mt: 2, fontStyle: "italic" }}>
              A custom-fabricated Oral Appliance for OSA is defined as one that
              is individually made for a specific patient (no other patient
              would be able to use this item) starting with basic materials. It
              involves substantial work to produce, usually by a specialized
              lab. It may involve the incorporation of some prefabricated
              components. It involves more than trimming, bending, or making
              other modifications to a substantially prefabricated item.
            </Typography>
          </Grid>
        </Grid>

        {/* Medical Necessity Statement */}
        <Grid
          container
          spacing={3}
          sx={{ m: 2, p: 2, backgroundColor: "#e8f4fd", borderRadius: 2 }}
        >
          <Grid item xs={12}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary"
              gutterBottom
            >
              Medical Necessity Statement
            </Typography>
            <Controller
              name="medicalNecessityStatement"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Medical Necessity Statement"
                  size="small"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Physician Information Section */}
        <Grid
          container
          spacing={3}
          sx={{ m: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}
        >
          <Grid item xs={12}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary"
              gutterBottom
            >
              Physician Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="physicianName"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Ordering Physician Name (Printed)"
                  size="small"
                  fullWidth
                  variant="outlined"
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="physicianNPI"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Physician's NPI Number"
                  size="small"
                  fullWidth
                  variant="outlined"
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="physicianAddress"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Physician's Address"
                  size="small"
                  fullWidth
                  variant="outlined"
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="physicianSignature"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Physician's Signature"
                  size="small"
                  fullWidth
                  variant="outlined"
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="physicianDate"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Date"
                  type="date"
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  {...field}
                  value={physicianDate || ""}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setPhysicianDate(e.target.value);
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Submit Button */}
        <Grid container justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: "#A574FD",
              color: "white",
              minWidth: 120,
              height: 40,
              "&:hover": {
                backgroundColor: "#8A5CE6",
              },
            }}
          >
            {submitting ? "Saving..." : "Save"}
          </Button>
        </Grid>
      </form>
    </Container>
  );
};

export default LetterOfNecessityForm;
